import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ArrowLeft, Mail, Send, FileText, Shield, AlertCircle, Check, Loader2, History, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { format } from 'date-fns';

type PolicyType = 'terms' | 'privacy' | 'both';

interface PolicyUpdate {
  id: string;
  policy_type: string;
  update_summary: string;
  effective_date: string;
  notification_sent: boolean | null;
  notification_sent_at: string | null;
  recipients_count: number | null;
  created_at: string;
}

export default function PolicyUpdateAdmin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [policyType, setPolicyType] = useState<PolicyType>('terms');
  const [updateSummary, setUpdateSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [isCounting, setIsCounting] = useState(false);
  const [emailsSent, setEmailsSent] = useState(false);
  const [latestUpdates, setLatestUpdates] = useState<PolicyUpdate[]>([]);
  const [isLoadingUpdates, setIsLoadingUpdates] = useState(true);

  useEffect(() => {
    fetchLatestUpdates();
  }, []);

  useEffect(() => {
    // Auto-fill summary when policy type changes
    const latestForType = latestUpdates.find(update => 
      update.policy_type === policyType || 
      (policyType === 'both' && (update.policy_type === 'terms' || update.policy_type === 'privacy'))
    );
    
    if (latestForType && !updateSummary) {
      // Only auto-fill if the current summary is empty
      setUpdateSummary(latestForType.update_summary);
    }
  }, [policyType, latestUpdates]);

  const fetchLatestUpdates = async () => {
    try {
      const { data, error } = await supabase
        .from('policy_updates')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setLatestUpdates((data || []) as PolicyUpdate[]);
      
      // Auto-fill with the most recent update for the current policy type
      const latestForType = (data || []).find((update: PolicyUpdate) => update.policy_type === policyType);
      if (latestForType) {
        setUpdateSummary(latestForType.update_summary);
      }
    } catch (error) {
      console.error('Error fetching policy updates:', error);
    } finally {
      setIsLoadingUpdates(false);
    }
  };

  const getPolicyLabel = (type: PolicyType) => {
    switch (type) {
      case 'terms': return 'Terms of Use';
      case 'privacy': return 'Privacy Policy';
      case 'both': return 'Terms of Use and Privacy Policy';
    }
  };

  const handlePolicyTypeChange = (type: PolicyType) => {
    setPolicyType(type);
    
    // Find latest update for this type
    const latestForType = latestUpdates.find(update => update.policy_type === type);
    if (latestForType) {
      setUpdateSummary(latestForType.update_summary);
    } else if (type === 'both') {
      // Combine summaries from both if selecting both
      const termsSummary = latestUpdates.find(u => u.policy_type === 'terms')?.update_summary || '';
      const privacySummary = latestUpdates.find(u => u.policy_type === 'privacy')?.update_summary || '';
      if (termsSummary || privacySummary) {
        setUpdateSummary(`Terms: ${termsSummary}\n\nPrivacy: ${privacySummary}`);
      }
    } else {
      setUpdateSummary('');
    }
  };

  const countRecipients = async () => {
    setIsCounting(true);
    try {
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (error) throw error;
      setRecipientCount(count || 0);
    } catch (error) {
      console.error('Error counting recipients:', error);
      toast({
        title: 'Error',
        description: 'Failed to count recipients. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCounting(false);
    }
  };

  const sendPolicyUpdateEmails = async () => {
    if (!updateSummary.trim()) {
      toast({
        title: 'Missing Summary',
        description: 'Please provide a summary of the policy changes.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      // First, save the policy update record
      const { data: user } = await supabase.auth.getUser();
      
      const { data: policyUpdate, error: insertError } = await supabase
        .from('policy_updates')
        .insert({
          policy_type: policyType,
          update_summary: updateSummary,
          created_by: user.user?.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Then send the emails
      const { data, error } = await supabase.functions.invoke('send-policy-update-notification', {
        body: {
          policyType,
          updateSummary,
        },
      });

      if (error) throw error;

      // Update the record with notification info
      await supabase
        .from('policy_updates')
        .update({
          notification_sent: true,
          notification_sent_at: new Date().toISOString(),
          recipients_count: data.sentCount,
        })
        .eq('id', policyUpdate.id);

      setEmailsSent(true);
      toast({
        title: 'Emails Sent Successfully',
        description: `Policy update notification sent to ${data.sentCount} users.`,
      });
    } catch (error) {
      console.error('Error sending policy update emails:', error);
      toast({
        title: 'Error',
        description: 'Failed to send policy update emails. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getLatestUpdateForType = (type: string): PolicyUpdate | undefined => {
    return latestUpdates.find(u => u.policy_type === type);
  };

  const getPolicyTypeLabel = (type: string): string => {
    switch (type) {
      case 'terms': return 'Terms of Use';
      case 'privacy': return 'Privacy Policy';
      case 'both': return 'Terms of Use and Privacy Policy';
      default: return 'Policy';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Policy Update Notifications</h1>
          <p className="text-muted-foreground">
            Send email notifications to all users about updates to Terms of Use or Privacy Policy
          </p>
        </div>

        {emailsSent ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Emails Sent Successfully!</h2>
              <p className="text-muted-foreground mb-6">
                All users have been notified about the {getPolicyLabel(policyType)} update.
              </p>
              <Button onClick={() => {
                setEmailsSent(false);
                setUpdateSummary('');
                fetchLatestUpdates();
              }}>
                Send Another Update
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Recent Policy Updates History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent Policy Updates
                </CardTitle>
                <CardDescription>
                  History of policy changes - summaries are auto-filled from the latest update
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingUpdates ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : latestUpdates.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No policy updates recorded yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {latestUpdates.slice(0, 5).map((update) => (
                      <div 
                        key={update.id} 
                        className="p-3 rounded-lg border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => {
                          if (update.policy_type === 'terms' || update.policy_type === 'privacy' || update.policy_type === 'both') {
                            setPolicyType(update.policy_type as PolicyType);
                          }
                          setUpdateSummary(update.update_summary);
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {update.policy_type === 'privacy' ? (
                              <Shield className="h-4 w-4 text-primary" />
                            ) : (
                              <FileText className="h-4 w-4 text-primary" />
                            )}
                            <span className="font-medium text-sm">
                              {getPolicyTypeLabel(update.policy_type)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(update.effective_date), 'MMM d, yyyy')}
                            {update.notification_sent && (
                              <span className="text-green-600 dark:text-green-400">• Sent</span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {update.update_summary}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Select Policy Type
                </CardTitle>
                <CardDescription>
                  Choose which policy has been updated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={policyType}
                  onValueChange={(value) => handlePolicyTypeChange(value as PolicyType)}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="terms" id="terms" />
                    <Label htmlFor="terms" className="flex items-center gap-2 cursor-pointer flex-1">
                      <FileText className="h-4 w-4 text-primary" />
                      Terms of Use
                      {getLatestUpdateForType('terms') && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          Last: {format(new Date(getLatestUpdateForType('terms')!.effective_date), 'MMM d, yyyy')}
                        </span>
                      )}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="privacy" id="privacy" />
                    <Label htmlFor="privacy" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Shield className="h-4 w-4 text-primary" />
                      Privacy Policy
                      {getLatestUpdateForType('privacy') && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          Last: {format(new Date(getLatestUpdateForType('privacy')!.effective_date), 'MMM d, yyyy')}
                        </span>
                      )}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="both" id="both" />
                    <Label htmlFor="both" className="flex items-center gap-2 cursor-pointer flex-1">
                      <Mail className="h-4 w-4 text-primary" />
                      Both (Terms of Use & Privacy Policy)
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Update Summary</CardTitle>
                <CardDescription>
                  Describe what changed in the policy (auto-filled from latest update - edit as needed)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Example: We've updated our data retention policies and added clearer language about how we use cookies..."
                  value={updateSummary}
                  onChange={(e) => setUpdateSummary(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Tip: Click on a recent update above to use it as a template
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Preview & Send</CardTitle>
                <CardDescription>
                  Review the notification before sending to all users
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <p className="font-medium">Email Preview:</p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Subject:</strong> Important Update to Our {getPolicyLabel(policyType)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Content:</strong> We&apos;ve made updates to our {getPolicyLabel(policyType)}. 
                    {updateSummary ? ` ${updateSummary.substring(0, 150)}${updateSummary.length > 150 ? '...' : ''}` : ' [Summary will appear here]'}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={countRecipients}
                    disabled={isCounting}
                  >
                    {isCounting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Count Recipients
                  </Button>
                  {recipientCount !== null && (
                    <span className="text-sm text-muted-foreground">
                      {recipientCount} users will receive this notification
                    </span>
                  )}
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Confirmation Required</AlertTitle>
                  <AlertDescription>
                    This will send an email to all registered users and record this update in the history. Please review the summary carefully before sending.
                  </AlertDescription>
                </Alert>

                <Button
                  onClick={sendPolicyUpdateEmails}
                  disabled={isLoading || !updateSummary.trim()}
                  className="w-full"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending Emails...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Policy Update Notification
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}